import { useState, createContext, useEffect } from 'react'
import { toast } from 'react-toastify'

import { inspectionBreakdownService } from '@/services/Inspection/InspectionBreakdownService'
import { precificationService } from '@/services/Precification'
import { precificationServiceV2 } from '@/services/Precification/v2'
import { vehicleService } from '@/services/utils/vehicleService'
import { vehicleServiceV2 } from '@/services/utils/v2/vehicleService'
import { utilityPreparationService } from '@/services/Preparation/utilityPreparationService'
import { inspectionPhotoServiceV2 } from '@/services/Inspection/v2/inspectionPhotoService'

import { useTableData } from '@/hooks/useTableData'
import { usePermission } from '@/hooks/usePermission'

import { PermissionAction, PermissionResource } from '@/types/permissions.types'
import { CONSTANTS } from '@/utils/CONSTANTS'
import {
  convertPrecificationMountData,
  convertPrecificationFromInspectionMountData
} from '@/pages/Precification/precification.factory'
import { precificationAttachedServiceV2 } from '@/services/PrecificationAttached/v2'
import { AxiosResponse } from 'axios'

interface IPrecificationContext {
  children: React.ReactNode
}

type IPrecificationContextData = {
  PRECIFICATION_DATA: any
  PRECIFICATION_ID: any
  INSPECTION_ID_LIST: any
  INSPECTION_LIST: any
  BOARD_LIST: any
  BOARD_OPTIONS: any
  OWNER_LIST: any
  OWNER_OPTIONS: any
  VEHICLE_PHOTO_DATA: any
  BREAKDOWN_PHOTO_DATA: any
  IMPLEMENTS: any
  VEHICLE_INSPECTION: any
  TOTALIZERS: any
  CAN_EDIT_VEHICLE_STATUS: boolean
  CAN_VIEW_VALOR_RESIDUAL: boolean
  CAN_VIEW_MARGINS: boolean
  CAN_UPDATE_VALOR_RESIDUAL: boolean
  GET_OWNER_LIST: () => {}
  GET_INSPECTION: (params?: any, isFilterApplied?: boolean) => void
  GET_TOTALIZERS: (params?: any) => Promise<void>
  SET_PRECIFICATION_ID: (params?: any) => void
  SET_IMPLEMENTS: (params?: any) => void
  GET_PRECIFICATION: (params?: any) => Promise<void>
  GET_SELECTS: (params?: any) => void
  GET_IMPLEMENTS: (params?: any) => void
  GET_VEHICLE_PHOTOS: (id: any) => Promise<void>
  GET_BREAKDOWN_PHOTOS: (id: any) => Promise<void>
  GET_VEHICLE_INSPECTION: (id: string) => Promise<void>
  GET_VEHICLE: (id: string) => Promise<void>
  SET_INSPECTION_ID_LIST: (list: any[]) => void
  SET_VEHICLE_INSPECTION: (data: any) => void
  SET_VEHICLE_PHOTO_DATA: (data: any) => void
  SET_BREAKDOWN_PHOTO_DATA: (data: any) => void
  SEND_FILE: (formData: any) => Promise<any>
  GET_PRICING_ATTACHMENT: (params: any) => Promise<any>
  DELETE_PRICING_ATTACHMENT: (id: string) => Promise<any>
  DOWNLOAD_PRICING_ATTACHMENT: (id: string) => Promise<any>
  PUT_MARGINS: (data: any) => Promise<any>
}

export const PrecificationContext = createContext(
  {} as IPrecificationContextData
)

export function PrecificationProvider({ children }: IPrecificationContext) {
  const [PRECIFICATION_DATA, SET_PRECIFICATION_DATA] = useState<any>(null)
  const [INSPECTION_ID_LIST, SET_INSPECTION_ID_LIST] = useState<any>(null)
  const [INSPECTION_LIST, SET_INSPECTION_LIST] = useState([])
  const [BOARD_LIST, SET_BOARD_LIST] = useState([])
  const [BOARD_OPTIONS, SET_BOARD_OPTIONS] = useState([])
  const [OWNER_LIST, SET_OWNER_LIST] = useState([])
  const [OWNER_OPTIONS, SET_OWNER_OPTIONS] = useState([])
  const [VEHICLE_PHOTO_DATA, SET_VEHICLE_PHOTO_DATA] = useState([])
  const [BREAKDOWN_PHOTO_DATA, SET_BREAKDOWN_PHOTO_DATA] = useState([])
  const [IMPLEMENTS, SET_IMPLEMENTS] = useState()
  const [PRECIFICATION_ID, SET_PRECIFICATION_ID] = useState()
  const [VEHICLE_INSPECTION, SET_VEHICLE_INSPECTION] = useState(null)
  const [TOTALIZERS, SET_TOTALIZERS] = useState(null)
  const { PERMISSIONS, HAS_PERMISSION } = usePermission()
  const [CAN_EDIT_VEHICLE_STATUS, SET_CAN_EDIT_VEHICLE_STATUS] = useState(false)
  const [CAN_VIEW_VALOR_RESIDUAL, SET_CAN_VIEW_VALOR_RESIDUAL] = useState(false)
  const [CAN_VIEW_MARGINS, SET_CAN_VIEW_MARGINS] = useState(false)
  const [CAN_UPDATE_VALOR_RESIDUAL, SET_CAN_UPDATE_VALOR_RESIDUAL] =
    useState(false)
  const { PER_PAGE, CURRENTE_PAGE, SORT_FIELD, SORT_ASCENDING } = useTableData()

  useEffect(() => {
    SET_DATA_SELECTS()
  }, [BOARD_LIST, OWNER_LIST])

  useEffect(() => {
    const canEditStatus = HAS_PERMISSION(
      PermissionResource.VEHICLE,
      PermissionAction.UPDATE
    )

    const canViewValorResidual = HAS_PERMISSION(
      PermissionResource.VALOR_RESIDUAL,
      PermissionAction.READ
    )
    const canUpdateValorResidual = HAS_PERMISSION(
      PermissionResource.VALOR_RESIDUAL,
      PermissionAction.UPDATE
    )

    SET_CAN_EDIT_VEHICLE_STATUS(canEditStatus)
    SET_CAN_UPDATE_VALOR_RESIDUAL(canUpdateValorResidual)
    SET_CAN_VIEW_VALOR_RESIDUAL(canViewValorResidual)

    const canViewMargens = HAS_PERMISSION(
      PermissionResource.MARGEM,
      PermissionAction.READ
    )
    SET_CAN_VIEW_MARGINS(canViewMargens)
  }, [PERMISSIONS, HAS_PERMISSION])

  async function GET_PRECIFICATION(params: any) {
    try {
      const res: any = await precificationServiceV2.get(
        PER_PAGE,
        CURRENTE_PAGE,
        convertPrecificationMountData(SORT_FIELD),
        SORT_ASCENDING,
        params
      )
      const precifications: any = res.data.data
      if (!precifications.rows?.length) {
        SET_PRECIFICATION_DATA(null)
        return toast.warning(CONSTANTS.filter.empty)
      }

      SET_PRECIFICATION_DATA(precifications)
      return precifications
    } catch (err) {
      console.log(err)
      toast.error('Não foi possível consultar os dados. Tente novamente.')
    }
  }

  async function GET_OWNER_LIST() {
    try {
      const res: any = await utilityPreparationService.getOwner()
      SET_OWNER_LIST(res?.data?.data ?? [])
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_INSPECTION(params?: any, isFilterApplied = false) {
    try {
      const formattedParams = {
        PageSize: PER_PAGE,
        Page: CURRENTE_PAGE,
        ...params,
        sortField: convertPrecificationFromInspectionMountData(SORT_FIELD),
        SortAscending: SORT_ASCENDING
      }
      const res: any = await precificationServiceV2.getVistorias(
        formattedParams
      )
      const inspections: any = res.data.data

      if (!inspections.rows?.length) {
        SET_INSPECTION_LIST([])
        toast.warning(CONSTANTS.filter.empty)
        return
      }
      if (inspections.rows?.length && params && Object.values(params)?.length)
        if (isFilterApplied) toast.success(CONSTANTS.filter.success)

      SET_INSPECTION_LIST(inspections)
    } catch (err) {
      console.log(err)
      toast.error('Não foi possível consultar os dados. Tente novamente.')
    }
  }

  async function GET_VEHICLE_PHOTOS(id: any) {
    try {
      const responsePhotos: any =
        await inspectionPhotoServiceV2.getPhotoParametrization(id)
      SET_VEHICLE_PHOTO_DATA(responsePhotos.data.data)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_BREAKDOWN_PHOTOS(id: any) {
    try {
      const res: any = await inspectionBreakdownService.getBreakdownPhotos(id)
      SET_BREAKDOWN_PHOTO_DATA(res.data.data)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_IMPLEMENTS(params: any) {
    try {
      const res: any = await vehicleService.getImplements(params)
      SET_IMPLEMENTS(res.data.data)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_VEHICLE_INSPECTION(id: string) {
    try {
      const res: any = await precificationServiceV2.getVistoriaVeiculo(id)
      const vehicleInspection = res.data.data
      SET_VEHICLE_INSPECTION(vehicleInspection)
      return vehicleInspection
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_VEHICLE(id: string) {
    try {
      const res: any = await vehicleServiceV2.getVeiculo(id)
      return res.data.data
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_SELECTS() {
    if (BOARD_LIST.length && OWNER_LIST.length) return null

    try {
      const res: any = await Promise.all([
        utilityPreparationService.getBoard(),
        utilityPreparationService.getOwner()
      ])

      SET_BOARD_LIST(() => res[0].data.data)
      SET_OWNER_LIST(() => res[1].data.data)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_TOTALIZERS(params?: any) {
    try {
      const res: any = await precificationService.getGeneralCosts(params)
      SET_TOTALIZERS(res.data.data)
      return res.data.data
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function SET_DATA_SELECTS() {
    let arrOfBoards: any = []
    let arrOfOwner: any = []

    BOARD_LIST.forEach((item: any) => {
      arrOfBoards.push(item?.descricao)
    })
    OWNER_LIST.forEach((item: any) => {
      arrOfOwner.push(item?.nome)
    })

    await SET_BOARD_OPTIONS(arrOfBoards)
    await SET_OWNER_OPTIONS(arrOfOwner)
  }

  async function SEND_FILE(formData: FormData): Promise<AxiosResponse | null> {
    try {
      const res = await precificationAttachedServiceV2.sendFile(formData)
      return res
    } catch (e: any) {
      toast.error(e.message)
      return null
    }
  }

  async function GET_PRICING_ATTACHMENT(params: any) {
    try {
      const res = await precificationAttachedServiceV2.getPricingAttachment(
        params
      )

      return res.data.data
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function DOWNLOAD_PRICING_ATTACHMENT(id: string) {
    try {
      const res =
        await precificationAttachedServiceV2.downloadPricingAttachment(id)

      return res
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function DELETE_PRICING_ATTACHMENT(id: string) {
    try {
      const res = await precificationAttachedServiceV2.deletePricingAttachment(
        id
      )

      return res
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function PUT_MARGINS(params: any) {
    try {
      const res = await precificationServiceV2.putMargins(params)

      return res
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  return (
    <PrecificationContext.Provider
      value={{
        SET_PRECIFICATION_ID,
        PRECIFICATION_ID,
        GET_PRECIFICATION,
        GET_INSPECTION,
        GET_SELECTS,
        GET_VEHICLE_PHOTOS,
        GET_BREAKDOWN_PHOTOS,
        GET_IMPLEMENTS,
        GET_OWNER_LIST,
        GET_VEHICLE_INSPECTION,
        GET_TOTALIZERS,
        GET_VEHICLE,
        IMPLEMENTS,
        OWNER_LIST,
        OWNER_OPTIONS,
        BOARD_LIST,
        BOARD_OPTIONS,
        INSPECTION_LIST,
        PRECIFICATION_DATA,
        VEHICLE_PHOTO_DATA,
        BREAKDOWN_PHOTO_DATA,
        VEHICLE_INSPECTION,
        INSPECTION_ID_LIST,
        TOTALIZERS,
        CAN_EDIT_VEHICLE_STATUS,
        CAN_VIEW_VALOR_RESIDUAL,
        CAN_VIEW_MARGINS,
        CAN_UPDATE_VALOR_RESIDUAL,
        SET_INSPECTION_ID_LIST,
        SET_IMPLEMENTS,
        SET_VEHICLE_INSPECTION,
        SET_VEHICLE_PHOTO_DATA,
        SET_BREAKDOWN_PHOTO_DATA,
        SEND_FILE,
        GET_PRICING_ATTACHMENT,
        DELETE_PRICING_ATTACHMENT,
        DOWNLOAD_PRICING_ATTACHMENT,
        PUT_MARGINS
      }}
    >
      {children}
    </PrecificationContext.Provider>
  )
}
